import React from 'react';
import { bool, object, string } from 'prop-types';

import { intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { FieldTextInput, FieldSelect } from '../../components';
import { LocationAutocomplete } from '../LocationAutocomplete/LocationAutocomplete';

import css from './StripePaymentAddress.module.css';

const StripePaymentAddress = props => {
  const { className, intl, disabled, form, fieldId, card, locale } = props;

  const optionalText = intl.formatMessage({
    id: 'StripePaymentAddress.optionalText',
  });

  const addressLine1Label = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Label',
  });
  const addressLine1Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1PlaceholderMarshall',
  });
  const addressLine1Required = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.addressLine1Required',
    })
  );
  const addressLine2Label = intl.formatMessage(
    { id: 'StripePaymentAddress.addressLine2Label' },
    { optionalText: optionalText }
  );
  const addressLine2Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine2PlaceholderMarshall',
  });
  const streetNumberLabel = intl.formatMessage({
    id: 'StripePaymentAddress.streetNumberLabelMarshall',
  });
  const streetNumberPlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.streetNumberPlaceholderMarshall',
  });
  const streetNumberRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.streetNumberRequired',
    })
  );

  const postalCodeLabel = intl.formatMessage({
    id: 'StripePaymentAddress.postalCodeLabelMarshall',
  });
  const postalCodePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.postalCodePlaceholderMarshall',
  });
  const postalCodeRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.postalCodeRequired',
    })
  );

  const cityLabel = intl.formatMessage({ id: 'StripePaymentAddress.cityLabelMarshall' });
  const cityPlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.cityPlaceholderMarshall',
  });
  const cityRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.cityRequired',
    })
  );

  const stateLabel = intl.formatMessage(
    { id: 'StripePaymentAddress.stateLabelMarshall' },
    { optionalText: optionalText }
  );
  const statePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.statePlaceholderMarshall',
  });

  const countryLabel = intl.formatMessage({ id: 'StripePaymentAddress.countryLabel' });
  const countryPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.countryPlaceholder' });
  const countryRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.countryRequired',
    })
  );

  const handleOnChange = event => {
    const value = event.target.value;
    form.change('postalCode', value);
    card.update({ value: { postalCode: value } });
  };

  // Use the language set in config.localization.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(locale).filter(c => c.code === 'GB');

  return (
    <div className={className ? className : css.root}>
      <div className={css.formRow}>
        <div className={css.addressLine}>
          <div className={css.label}>
            <label>{addressLine1Label}</label>
          </div>
          <FieldTextInput
            id={`${fieldId}.addressLine1`}
            name="addressLine1"
            disabled={disabled}
            className={css.fieldFullWidth}
            type="text"
            autoComplete="billing address-line1"
            component={LocationAutocomplete}
            includeStreetNumber={false}
            countryCode="GB"
            label={addressLine1Label}
            placeholder={addressLine1Placeholder}
            handleLocationUpdate={location => {
              form.change(`addressLine1`, location.street);
              form.change(`streetNumber`, location.streetNumber);
              form.change(`city`, location.city);
              form.change(`postalCode`, location.zipCode);
              form.change(`state`, location.county);
            }}
            validate={addressLine1Required}
            onUnmount={() => form.change('addressLine1', undefined)}
          />
        </div>

        <FieldTextInput
          id={`${fieldId}.streetNumber`}
          name="streetNumber"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-street-number"
          label={streetNumberLabel}
          placeholder={streetNumberPlaceholder}
          validate={streetNumberRequired}
          onUnmount={() => form.change('streetNumber', undefined)}
        />
      </div>
      <FieldTextInput
        id={`${fieldId}.addressLine2`}
        name="addressLine2"
        disabled={disabled}
        className={css.fieldFullWidth}
        type="text"
        autoComplete="billing address-line2"
        label={addressLine2Label}
        placeholder={addressLine2Placeholder}
        onUnmount={() => form.change('addressLine2', undefined)}
      />
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.postalCode`}
          name="postalCode"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing postal-code"
          label={postalCodeLabel}
          placeholder={postalCodePlaceholder}
          validate={postalCodeRequired}
          onUnmount={() => form.change('postalCode', undefined)}
          onChange={event => handleOnChange(event)}
        />

        <FieldTextInput
          id={`${fieldId}.city`}
          name="city"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level2"
          label={cityLabel}
          placeholder={cityPlaceholder}
          validate={cityRequired}
          onUnmount={() => form.change('city', undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.state`}
          name="state"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level1"
          label={stateLabel}
          placeholder={statePlaceholder}
          onUnmount={() => form.change('state', undefined)}
        />

        <FieldSelect
          id={`${fieldId}.country`}
          name="country"
          disabled={disabled}
          className={css.field}
          label={countryLabel}
          validate={countryRequired}
        >
          <option disabled value="">
            {countryPlaceholder}
          </option>
          {countryCodes.map(country => {
            return (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            );
          })}
        </FieldSelect>
      </div>
    </div>
  );
};
StripePaymentAddress.defaultProps = {
  country: null,
  disabled: false,
  fieldId: null,
};

StripePaymentAddress.propTypes = {
  country: string,
  disabled: bool,
  form: object.isRequired,
  fieldId: string,
  locale: string.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default StripePaymentAddress;
